import type { Theme } from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  InputAdornment,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { DIALOG_WIDTH } from "../../constants";
import StandardDialogTitle from "./StandardDialogTitle";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Schemas } from "../../schemas";
import StandardDialogActions from "./StandardDialogActions";
import z from "zod";
import * as React from "react";
import { CloseDialogHandler } from "../../contexts/DialogContext";
import { useCreateAlertSchema } from "../../hooks/mutation/create/useCreateAlertProvider";

const schema = Schemas.alertSchema.omit({ id: true, system: true });

const NewAlertSchemaDialog: React.FC<{
  onDialogClosed: CloseDialogHandler;
}> = ({ onDialogClosed }) => {
  const isSmallScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));
  const { mutateAsync: createAlertSchema, isPending: isCreating } = useCreateAlertSchema();
  const [submitError, setSubmitError] = React.useState<string | null>(null);
  const [foreignKeyInputValue, setForeignKeyInputValue] =
    React.useState<string>("");

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: {
      statusMappingCritical: "critical",
      statusMappingWarning: "warning",
      statusMappingResolved: "resolved"
    },
    mode: "onBlur",
  });


  const onSubmit = async (data: z.infer<typeof schema>) => {
    try {
      setSubmitError(null);
      await createAlertSchema(data);
      reset({});
      onDialogClosed({ message: "Alert schema created!", severity: "success" });
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Something went wrong.";
      setSubmitError(msg);
    }
  };

  const onInvalid = (errs: Record<string, unknown>) => {
    console.error(errs);
    setSubmitError("Error during submit");
  }

  const onClose = () => {
    setSubmitError(null);
    reset({});
    onDialogClosed();
  };

  return (
    <Dialog
      open={true}
      onClose={onClose}
      fullWidth
      fullScreen={isSmallScreen}
      PaperProps={{ sx: { maxWidth: isSmallScreen ? undefined : DIALOG_WIDTH.lg } }}
    >
      <StandardDialogTitle
        title={"New Alert Schema"}
        isSmallScreen={isSmallScreen}
        onClose={onClose}
      />

      <Box
        component="form"
        id="new-alert-schema-form"
        onSubmit={handleSubmit(onSubmit, onInvalid)}
      >
        <DialogContent sx={{ overflowX: "hidden" }}>
          <Box sx={{ width: "100%" }}>
            <Stack sx={{ mb: 1.5 }}>
              <Typography variant="body2" color="text.secondary">
                Create a alert schema and optionally declare its dependencies.
              </Typography>
            </Stack>

            <Divider sx={{ mb: 1.5 }} />

            <Stack spacing={2.25} sx={{ width: "100%" }}>

              <FormControl>
                <FormLabel>Name</FormLabel>
                <TextField
                  size="small"
                  placeholder="e.g. Coralogix"
                  error={!!errors.name}
                  {...register("name", {
                    onChange: () => submitError && setSubmitError(null),
                  })}
                />
                <FormHelperText error={!!errors.name}>
                  {errors.name?.message ?? " "}
                </FormHelperText>
              </FormControl>

              <FormControl error={!!errors.foreignKeyProps}>
                <FormLabel>Foreign key</FormLabel>

                <Controller
                  name="foreignKeyProps"
                  control={control}
                  render={({ field }) => {
                    const confirmAdd = () => {
                      const v = foreignKeyInputValue.trim();
                      if (!v) {return;}

                      const current = ((field.value ?? []) as string[])
                        .map((x) => x.trim())
                        .filter(Boolean);

                      const next = Array.from(new Set([...current, v]));
                      field.onChange(next);

                      setForeignKeyInputValue("");
                      if (submitError) {setSubmitError(null);}
                    };

                    return (
                      <Autocomplete
                        multiple
                        freeSolo
                        disableClearable
                        options={[] as string[]}
                        value={(field.value ?? []) as string[]}

                        inputValue={foreignKeyInputValue}
                        onInputChange={(_, newInput, reason) => {
                          if (reason === "reset") {return;}
                          setForeignKeyInputValue(newInput ?? "");
                        }}

                        onChange={(_, newValue) => {
                          const unique = Array.from(
                            new Set(newValue.map((v) => v.trim()).filter(Boolean))
                          );
                          field.onChange(unique);
                          if (submitError) {setSubmitError(null);}
                        }}

                        renderTags={(value, getTagProps) =>
                          value.map((option, index) => (
                            <Chip
                              label={option}
                              size="small"
                              {...getTagProps({ index })}
                              key={`${option}-${index}`}
                            />
                          ))
                        }

                        renderInput={(params) => (
                          <TextField
                            {...params}
                            size="small"
                            placeholder="Type a key and press Enter or Add"
                            error={!!errors.foreignKeyProps}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                confirmAdd();
                              }
                            }}
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  <InputAdornment position="start">
                                    <Button
                                      color="primary"
                                      variant="text"
                                      size="small"
                                      disabled={foreignKeyInputValue.trim().length === 0}
                                      onMouseDown={(e) => e.preventDefault()}
                                      onClick={confirmAdd}
                                      startIcon={<AddRoundedIcon fontSize="small" />}
                                      sx={{
                                        minWidth: 78,
                                        height: 30,
                                        px: 1.2,
                                        borderRadius: "8px",
                                        border: "1px solid rgba(34, 211, 238, 0.34)",
                                        backgroundColor: "rgba(6, 182, 212, 0.12)",
                                        color: "rgba(236, 254, 255, 0.96)",
                                        fontWeight: 600,
                                        "&:hover": {
                                          borderColor: "rgba(34, 211, 238, 0.5)",
                                          backgroundColor: "rgba(6, 182, 212, 0.18)",
                                        },
                                        "&.Mui-disabled": {
                                          borderColor: "rgba(71, 85, 105, 0.55)",
                                          backgroundColor: "rgba(15, 23, 42, 0.45)",
                                          color: "rgba(148, 163, 184, 0.75)",
                                        },
                                      }}
                                    >
                                      Add
                                    </Button>
                                  </InputAdornment>
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            }}
                          />
                        )}
                      />
                    );
                  }}
                />

                <FormHelperText>
                  {errors.foreignKeyProps?.message ?? " "}
                </FormHelperText>
              </FormControl>

              <FormControl>
                <FormLabel>Status property</FormLabel>
                <TextField
                  size="small"
                  placeholder="The property which identify the status"
                  error={!!errors.statusProp}
                  {...register("statusProp", {
                    onChange: () => submitError && setSubmitError(null),
                  })}
                />
                <FormHelperText error={!!errors.statusProp}>
                  {errors.statusProp?.message ?? " "}
                </FormHelperText>
              </FormControl>

              <Typography>Status property override</Typography>

              <FormControl>
                <FormLabel>Resolved status</FormLabel>
                <TextField
                  size="small"
                  placeholder="resolved"
                  error={!!errors.statusMappingResolved}
                  {...register("statusMappingResolved", {
                    onChange: () => submitError && setSubmitError(null),
                  })}
                />
                <FormHelperText error={!!errors.statusMappingResolved}>
                  {errors.statusMappingResolved?.message ?? " "}
                </FormHelperText>
              </FormControl>
              
              <FormControl>
                <FormLabel>Warning status</FormLabel>
                <TextField
                  size="small"
                  placeholder="warning"
                  error={!!errors.statusMappingWarning}
                  {...register("statusMappingWarning", {
                    onChange: () => submitError && setSubmitError(null),
                  })}
                />
                <FormHelperText error={!!errors.statusMappingWarning}>
                  {errors.statusMappingWarning?.message ?? " "}
                </FormHelperText>
              </FormControl>

              <FormControl>
                <FormLabel>Criticial status</FormLabel>
                <TextField
                  size="small"
                  placeholder="criticial"
                  error={!!errors.statusMappingCritical}
                  {...register("statusMappingCritical", {
                    onChange: () => submitError && setSubmitError(null),
                  })}
                />
                <FormHelperText error={!!errors.statusMappingCritical}>
                  {errors.statusMappingCritical?.message ?? " "}
                </FormHelperText>
              </FormControl>

              <FormControl>
                <FormLabel>Description</FormLabel>
                <TextField
                  size="small"
                  multiline
                  minRows={2}
                  placeholder="Short description of the system"
                  error={!!errors.description}
                  {...register("description", {
                    onChange: () => submitError && setSubmitError(null),
                  })}
                />
                <FormHelperText error={!!errors.description}>
                  {errors.description?.message ?? " "}
                </FormHelperText>
              </FormControl>

              {submitError && (
                <FormHelperText error sx={{ fontSize: 13, m: 0 }}>
                  {submitError}
                </FormHelperText>
              )}
            </Stack>
          </Box>
        </DialogContent>

        <StandardDialogActions
          primaryText={"Create"}
          primaryIcon={<></>}
          onCancel={onClose}
          loading={isCreating}
          disabled={isCreating}
        />
      </Box>
    </Dialog>
  );
};

export default NewAlertSchemaDialog;
